import { Chain, CardGrid, Figure } from './primitives'

/* ============================ DELIVERY ============================ */

const PHASES = [
  { label: 'Sprint 0', name: 'Baseline', weeks: 2, tone: 'ink' },
  { label: 'S1–S3', name: 'Core Platform', weeks: 6, tone: 'river' },
  { label: 'S4–S6', name: 'Acquisition', weeks: 6, tone: 'river' },
  { label: 'S7–S10', name: 'Land Management', weeks: 8, tone: 'land' },
  { label: 'S11–S12', name: 'Disposition', weeks: 4, tone: 'land' },
  { label: 'S13–S14', name: 'Migration & Hardening', weeks: 4, tone: 'earth' },
  { label: 'S15', name: 'UAT & Training', weeks: 2, tone: 'earth' },
  { label: 'S16', name: 'Go-Live & Hypercare', weeks: 2, tone: 'ink' },
] as const

const TOTAL_WEEKS = PHASES.reduce((n, p) => n + p.weeks, 0)

export function ProgramTimeline() {
  return (
    <Figure
      label={`A ${TOTAL_WEEKS}-week program from January 1 2027 to August 26 2027, divided into eight phases from Sprint 0 baseline through go-live and hypercare.`}
      wide
    >
      <div className="timeline">
        <div className="timeline__scale">
          <span>Jan 1, 2027</span>
          <span className="timeline__scale-mid">34 weeks</span>
          <span>Aug 26, 2027</span>
        </div>
        <ol className="timeline__bar">
          {PHASES.map((p) => (
            <li
              key={p.label}
              className={`timeline__phase timeline__phase--${p.tone}`}
              style={{ flexGrow: p.weeks }}
            >
              <span className="timeline__phase-label">{p.label}</span>
              <span className="timeline__phase-name">{p.name}</span>
            </li>
          ))}
        </ol>
        <ul className="timeline__marks">
          <li><strong>Jan 15</strong> Formal kickoff</li>
          <li><strong>Aug 12</strong> UAT / readiness</li>
          <li><strong>Aug 13</strong> Go-live</li>
          <li><strong>Aug 26</strong> Hypercare close</li>
        </ul>
      </div>
    </Figure>
  )
}

export function SprintRoadmap() {
  return (
    <Figure label="Eight delivery phases, each producing visible business capability, with architecture, GIS, integration, migration and QA running as parallel cross-cutting workstreams.">
      <div className="roadmap">
        <CardGrid
          columns={4}
          tone="mixed"
          cards={PHASES.map((p) => ({
            meta: p.label,
            title: p.name,
            detail: `${p.weeks} weeks`,
          }))}
        />
        <div className="roadmap__parallel">
          <span className="roadmap__parallel-title">Cross-cutting workstreams, running in parallel</span>
          <ul>
            {['Architecture', 'GIS', 'Integration', 'Data migration', 'Quality assurance', 'Backlog refinement'].map(
              (w) => (
                <li key={w}>{w}</li>
              ),
            )}
          </ul>
        </div>
      </div>
    </Figure>
  )
}

const MILESTONES = [
  { id: 'M1', name: 'Implementation Baseline & Kickoff', date: 'Jan 14–15', pct: 10 },
  { id: 'M2', name: 'Core Platform Baseline', date: 'Feb 25', pct: 15 },
  { id: 'M3', name: 'Land Acquisition Increment', date: 'Apr 8', pct: 20 },
  { id: 'M4', name: 'Land Management Increment', date: 'Jun 3', pct: 20 },
  { id: 'M5', name: 'Land Disposition Increment', date: 'Jul 1', pct: 10 },
  { id: 'M6', name: 'Migration & System Consolidation', date: 'Jul 29', pct: 15 },
  { id: 'M7', name: 'UAT & Production Readiness', date: 'Aug 12', pct: 2 },
  { id: 'M8', name: 'Production Go-Live', date: 'Aug 13', pct: 3 },
  { id: 'M9', name: 'Hypercare & Final Acceptance', date: 'Aug 26', pct: 5 },
]

export function MilestoneRail() {
  return (
    <Figure
      label="Nine milestones from M1 Implementation Baseline in January to M9 Hypercare Completion and Final Acceptance in August 2027."
      wide
    >
      <ol className="rail">
        {MILESTONES.map((m) => (
          <li key={m.id} className="rail__item">
            <span className="rail__dot" aria-hidden="true" />
            <span className="rail__id">{m.id}</span>
            <span className="rail__name">{m.name}</span>
            <span className="rail__date">{m.date}, 2027</span>
          </li>
        ))}
      </ol>
    </Figure>
  )
}

export function PaymentRail() {
  return (
    <Figure
      label="Milestone payment distribution across M1 to M9, totalling US$610,000. The three largest payments attach to the acquisition, management and core platform increments; the final 10% is retained across production readiness, go-live and hypercare."
      wide
    >
      <div className="payrail">
        <ol className="payrail__bars">
          {MILESTONES.map((m) => (
            <li key={m.id} className="payrail__bar" style={{ flexGrow: m.pct }}>
              <span className="payrail__fill" style={{ height: `${(m.pct / 20) * 100}%` }} />
              <span className="payrail__id">{m.id}</span>
              <span className="payrail__pct">{m.pct}%</span>
            </li>
          ))}
        </ol>
        <p className="payrail__note">
          <strong>10% retained</strong> across M7, M8 and M9 — production readiness, go-live and hypercare — so final
          payment follows production stabilization.
        </p>
      </div>
    </Figure>
  )
}

export function StakeholderMap() {
  return (
    <Figure label="Governance map: District stakeholder groups on one side, Merlion delivery responsibility areas on the other, meeting at shared governance.">
      <div className="stakeholders">
        <div className="stakeholders__col">
          <span className="stakeholders__title">District</span>
          <ul>
            {[
              'Project / product leadership',
              'Land Acquisition',
              'Land Management',
              'GIS',
              'IT / infrastructure / security',
              'Finance',
              'Records / documents',
              'UAT and operational users',
            ].map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
        <div className="stakeholders__centre">
          <span className="stakeholders__centre-mark">Shared governance</span>
          <span className="stakeholders__centre-note">
            Sprint reviews · backlog ownership · change control · risk escalation
          </span>
        </div>
        <div className="stakeholders__col">
          <span className="stakeholders__title">Merlion</span>
          <ul>
            {[
              'Project management / Scrum',
              'Solution architecture',
              'Requirements / business analysis',
              'Full-stack engineering',
              'GIS',
              'Data migration / ETL',
              'QA',
              'UX/UI',
              'Training / documentation',
              'Infrastructure / support',
            ].map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
      </div>
    </Figure>
  )
}

export function MigrationPipeline() {
  return (
    <Figure
      label="Migration pipeline: source assessment, mapping, transformation, migration, reconciliation, exception resolution, District validation."
      wide
    >
      <div className="migration">
        <Chain
          tone="earth"
          emphasis={4}
          steps={[
            { label: 'Source assessment', detail: 'Profiling' },
            { label: 'Mapping', detail: 'Source to target' },
            { label: 'Transformation', detail: 'Rules' },
            { label: 'Migration', detail: 'Controlled import' },
            { label: 'Reconciliation', detail: 'Counts match' },
            { label: 'Exception resolution', detail: 'Explicit disposition' },
            { label: 'District validation', detail: 'Sign-off' },
          ]}
        />
        <p className="migration__note">
          Repeatable, so test migrations run before the production cutover. The District confirmed that reconciliation
          with documented exceptions and sign-off is an acceptable standard for the zero-error requirement.
        </p>
      </div>
    </Figure>
  )
}

const RISKS = [
  { risk: 'Source data quality / migration exceptions', impact: 'high', likelihood: 'medium' },
  { risk: 'Integration / API availability', impact: 'high', likelihood: 'medium' },
  { risk: 'GIS dependency and configuration', impact: 'high', likelihood: 'low' },
  { risk: 'District stakeholder availability', impact: 'medium', likelihood: 'medium' },
  { risk: 'Environment / access readiness', impact: 'medium', likelihood: 'medium' },
  { risk: 'Scope refinement / scope creep', impact: 'medium', likelihood: 'medium' },
  { risk: 'UAT timing / acceptance defects', impact: 'medium', likelihood: 'low' },
  { risk: 'Adoption / operational readiness', impact: 'medium', likelihood: 'low' },
] as const

export function RiskMatrix() {
  return (
    <Figure label="Eight identified implementation risks, each rated by impact and likelihood, each with a stated mitigation.">
      <ul className="risks">
        {RISKS.map((r) => (
          <li key={r.risk} className="risks__item">
            <span className="risks__name">{r.risk}</span>
            <span className="risks__tags">
              <span className={`risks__tag risks__tag--${r.impact}`}>Impact: {r.impact}</span>
              <span className={`risks__tag risks__tag--${r.likelihood}`}>Likelihood: {r.likelihood}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="risks__note">Mitigations for each are listed under Explore Details.</p>
    </Figure>
  )
}

export function SupportContinuum() {
  return (
    <Figure
      label="Support continuum: go-live, hypercare, twelve-month support, continuous improvement."
      wide
    >
      <Chain
        tone="land"
        steps={[
          { label: 'Go-live', detail: 'Aug 13, 2027' },
          { label: 'Hypercare', detail: 'Through Aug 26' },
          { label: '12-month support', detail: 'L1 + L2, M–F 8–5 ET' },
          { label: 'Continuous improvement', detail: 'Renewal by agreement' },
        ]}
      />
    </Figure>
  )
}

/* ============================ TEAM ============================ */

const ARCHITECT_STRANDS = [
  {
    title: 'National-scale public platforms',
    points: [
      'Architect and Technical Manager, Agricultural Management Platform (PGA) — CNA with MAPA',
      'Architecture of Brazil’s agrochemical registration system, integrating three regulatory bodies',
      'Solution architect, Fiocruz / SES-DF public-health digital transformation',
    ],
  },
  {
    title: 'Enterprise Asset Management',
    points: [
      'Founded ID2 Tecnologia on the concept “Everything is an Asset”, built on IBM Maximo',
      'IBM “Innovation” award, 2010',
      'Structured the IBM Maximo practice at Magna Sistemas; SERPRO Service Desk architecture',
    ],
  },
  {
    title: 'Products and applied AI',
    points: [
      'Izzy ERP / Construction / CRM product family for the U.S. market',
      'AI applied to CRM, insight generation, schedule analysis and project risk',
    ],
  },
]

export function ArchitectProfile() {
  return (
    <Figure label="Jesse Rovira, founder and Technical Leader of Merlion IT, assigned to LAMS as Software Architect and Technical Lead for the life of the project. Three strands of experience: national-scale public platforms, Enterprise Asset Management, and products with applied AI.">
      <ul className="experts">
        <li className="experts__card experts__card--lead">
          <span className="experts__role">Founder &amp; Technical Leader, Merlion IT</span>
          <span className="experts__name">Jesse Rovira</span>
          <ul className="experts__points">
            <li>Software Architect / Technical Lead on LAMS — project-long</li>
            <li>20+ years in software development, solution architecture, enterprise integration, workflow and data architecture</li>
            <li>Accountable for solution, application, data, integration, security and AI architecture</li>
          </ul>
        </li>
        {ARCHITECT_STRANDS.map((s) => (
          <li key={s.title} className="experts__card">
            <span className="experts__role">{s.title}</span>
            <ul className="experts__points">
              {s.points.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </Figure>
  )
}

const TEAM = [
  {
    role: 'Software Architect / Technical Lead',
    name: 'Jesse Rovira',
    line: 'Founder, Merlion IT · 20+ yrs architecture, integration and workflow systems · national platforms · EAM / Maximo',
    involvement: 'Project-long',
  },
  {
    role: 'Project Manager / Scrum Master',
    name: 'Felipe Nonato Marinho',
    line: '15+ yrs delivery · 9+ yrs Agile/Scrum · public-sector transformation · Scrum.org certified',
    involvement: 'Project-long',
  },
  {
    role: 'Fullstack Developer Sr.',
    name: 'Pedro Bucci',
    line: 'React/TypeScript · Node.js · SQL Server · Docker · 8 yrs agricultural traceability systems',
    involvement: 'Primary development lifecycle',
  },
  {
    role: 'GIS Analyst Sr.',
    name: 'Carlos Ribeiro',
    line: 'Agronomist · 20+ yrs geoprocessing, land regularization, environmental licensing · ArcGIS, QGIS',
    involvement: 'Discovery through system validation',
  },
  {
    role: 'UX/UI Designer Sr.',
    name: 'Bruno Freitas',
    line: '16+ yrs IT · React, Next.js, TypeScript, design systems · public sector and enterprise',
    involvement: 'Discovery and functional delivery',
  },
]

export function TeamGrid() {
  return (
    <Figure label="Five named professionals assigned to key delivery roles, each with their relevance to LAMS and planned involvement.">
      <ul className="team">
        {TEAM.map((t) => (
          <li key={t.name} className="team__card">
            <span className="team__role">{t.role}</span>
            <span className="team__name">{t.name}</span>
            <span className="team__line">{t.line}</span>
            <span className="team__involve">{t.involvement}</span>
          </li>
        ))}
      </ul>
    </Figure>
  )
}

const MATRIX_DOMAINS = [
  'Software platform',
  'Public sector',
  'GIS',
  'Land',
  'EAM',
  'Workflow',
  'Data migration',
  'AI',
]

const MATRIX: Record<string, string[]> = {
  'Jesse Rovira': ['Software platform', 'Public sector', 'EAM', 'Workflow', 'AI'],
  'Carlos Ribeiro': ['Public sector', 'GIS', 'Land'],
  'Pedro Bucci': ['Software platform', 'Workflow', 'Data migration'],
  'Felipe Marinho': ['Public sector', 'Workflow', 'EAM'],
  'Bruno Freitas': ['Software platform', 'Public sector', 'EAM'],
}

export function ExpertiseMatrix() {
  return (
    <Figure label="Expertise matrix mapping each named team member against software platform, public sector, GIS, land, EAM, workflow, data migration and AI.">
      <div className="table-wrap">
        <table className="matrix">
          <thead>
            <tr>
              <th scope="col">Team member</th>
              {MATRIX_DOMAINS.map((d) => (
                <th key={d} scope="col">
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(MATRIX).map(([person, domains]) => (
              <tr key={person}>
                <th scope="row">{person}</th>
                {MATRIX_DOMAINS.map((d) => {
                  const has = domains.includes(d)
                  return (
                    <td key={d} className={has ? 'matrix__on' : 'matrix__off'}>
                      <span className="visually-hidden">{has ? 'Yes' : 'No'}</span>
                      <span aria-hidden="true">{has ? '●' : '·'}</span>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Figure>
  )
}

export function ReferenceCards() {
  return (
    <Figure label="Three project references: the Agricultural Management Platform for CNA and MAPA, the AgriTrace animal and plant traceability platforms for CNA and Instituto CNA, and Avenyx.AI, a United States international education operations platform.">
      <ul className="refs">
        {[
          {
            name: 'Agricultural Management Platform (PGA)',
            client: 'CNA / Ministry of Agriculture and Livestock (MAPA), Brazil',
            detail:
              "National-scale agricultural management and integration platform developed through cooperation between CNA and MAPA. It consolidated animal-health and traceability information from Brazil's 27 federative units into a unified environment, supporting animal transit (e-GTA), regulatory workflows, monitoring, reporting, and integration with government systems.",
            role: 'Members of the proposed team contributed to solution architecture, technical management, engineering and long-term platform evolution.',
            status: 'Completed with schedule variance across approximately six years',
            link: 'beefpoint.com.br',
            href: 'https://beefpoint.com.br/plataforma-de-gestao-agropecuaria-a-nova-ferramenta-de-transparencia-e-credibilidade-da-pecuaria-brasileira/',
          },
          {
            name: 'AgriTrace Animal & Vegetal Traceability Platforms',
            client: 'CNA / Instituto CNA, Brazil',
            detail:
              'National-scale traceability platforms developed within the CNA / Instituto CNA agricultural technology ecosystem. AgriTrace Animal supports voluntary livestock traceability and certification protocols, while AgriTrace Vegetal supports traceability of fresh plant products and compliance-oriented records across geographically distributed participants in the agricultural chain.',
            role: 'Proposed team members participated in technical coordination, solution architecture, development, implementation and evolution of these platforms.',
            status: 'Vegetal: on time, within budget · Animal: material scope evolution absorbed without redesign',
            link: 'cnabrasil.org.br',
            href: 'https://cnabrasil.org.br/projetos-e-programas/programa-rastreabilidade-animal',
          },
          {
            name: 'Avenyx.AI — International Education Operations Platform',
            client: 'Avenyx.AI / Conexus Global Consulting, United States',
            detail:
              'U.S.-based international education technology platform supporting student recruitment and admissions operations. The solution centralizes CRM and workflow capabilities for student and agency onboarding, applicant tracking, institutional relationships, operational coordination, and integration with external education services in a multi-user web environment.',
            role: "Demonstrates the proposed team's experience designing and delivering a modern, workflow-driven software platform for a U.S. client environment.",
            status: 'Currently in delivery',
            link: 'avenyx.ai',
            href: 'https://avenyx.ai',
          },
        ].map((r) => (
          <li key={r.name} className="refs__card">
            <span className="refs__name">{r.name}</span>
            <span className="refs__client">{r.client}</span>
            <span className="refs__detail">{r.detail}</span>
            <span className="refs__role">{r.role}</span>
            <span className="refs__status">{r.status}</span>
            <a className="refs__link" href={r.href} target="_blank" rel="noreferrer noopener">
              {r.link}
            </a>
          </li>
        ))}
      </ul>
    </Figure>
  )
}

/* ============================ INVESTMENT ============================ */

export function InvestmentCards() {
  return (
    <Figure label="Platform Development US$610,000; Technical Support for twelve months US$40,000; total US$650,000, as offered on Form A.">
      <ul className="invest">
        <li className="invest__card">
          <span className="invest__label">Platform Development</span>
          <span className="invest__figure">US$ 610,000</span>
          <span className="invest__note">Not-to-exceed · fixed scope · milestone-based</span>
        </li>
        <li className="invest__card">
          <span className="invest__label">Technical Support (12 months)</span>
          <span className="invest__figure">US$ 40,000</span>
          <span className="invest__note">Level 1 + Level 2 · M–F 8:00 a.m.–5:00 p.m. ET · separate agreement</span>
        </li>
        <li className="invest__card invest__card--total">
          <span className="invest__label">Total</span>
          <span className="invest__figure">US$ 650,000</span>
          <span className="invest__note">Implementation plus first twelve months of support</span>
        </li>
      </ul>
    </Figure>
  )
}

export function DeliverableCategories() {
  return (
    <Figure label="Deliverable categories covered by the investment: core platform, acquisition, management, disposition, GIS and integrations, migration, UAT and training, go-live and hypercare, and twelve-month support as a separate line.">
      <CardGrid
        columns={3}
        tone="mixed"
        cards={[
          { title: 'Core Platform', detail: 'Identity, land record, workflow, reporting, notifications' },
          { title: 'Land Acquisition', detail: 'Intake through closing and transition' },
          { title: 'Land Management', detail: 'Planning, work, natural resources, timber' },
          { title: 'Land Disposition', detail: 'Evaluation through closing' },
          { title: 'GIS & Integrations', detail: 'ArcGIS, CivicPlus, PaperVision, AccuFund, PERCH, Entra ID' },
          { title: 'Data Migration', detail: 'Conversion, reconciliation, exception resolution' },
          { title: 'UAT & Training', detail: 'Acceptance testing, Quick Steps, knowledge transfer' },
          { title: 'Go-Live & Hypercare', detail: 'Deployment, smoke validation, stabilization' },
          { title: '12-Month Support', detail: 'Separate commercial line — US$40,000' },
        ]}
      />
    </Figure>
  )
}

export function Accountability() {
  return (
    <Figure label="Four accountability commitments: service credits for late milestones, credits for failed release items within tolerance, a penalty for a failed release, and termination exposure on two consecutive failures.">
      <ul className="accountability">
        {[
          {
            trigger: 'Milestone more than 5 business days late',
            consequence: '1% service credit per additional full business week',
            cap: 'Capped at 5% of that milestone payment',
          },
          {
            trigger: 'Release items failing acceptance, 1%–10%',
            consequence: '1% credit per failed item',
            cap: 'Capped at 10% of the release payment',
          },
          {
            trigger: 'More than 10% of release items fail',
            consequence: '5% penalty on total implementation price',
            cap: 'Plus corrective recovery plan; release not accepted until retested',
          },
          {
            trigger: 'Two consecutive failed releases',
            consequence: 'Project subject to termination',
            cap: 'For vendor performance failure',
          },
        ].map((a) => (
          <li key={a.trigger} className="accountability__row">
            <span className="accountability__trigger">{a.trigger}</span>
            <span className="accountability__arrow" aria-hidden="true">
              →
            </span>
            <span className="accountability__consequence">
              {a.consequence}
              <span className="accountability__cap">{a.cap}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="accountability__note">
        Applies to delays attributable to Merlion-controlled activities. Approved scope changes, District-caused delays
        and third-party dependencies are excluded, and consequences are not stacked for the same event.
      </p>
    </Figure>
  )
}

/* ============================ CLOSING ============================ */

export function ClosingLifecycle() {
  return (
    <Figure
      label="The lifecycle returns, now with the complete platform underneath: acquisition, land management and disposition over GIS, work, documents, contracts, measures, reporting and audit, over the governed platform."
      wide
    >
      <div className="closing">
        <Chain steps={[{ label: 'Acquisition' }, { label: 'Land Management' }, { label: 'Disposition' }]} />
        <ul className="closing__shared">
          {['GIS', 'Work', 'Documents', 'Contracts', 'Measures', 'Reporting', 'Audit'].map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
        <div className="closing__platform">
          <span>LAMS Studio · Runtime · Operations</span>
          <span className="closing__platform-note">Governed, versioned, evolvable</span>
        </div>
      </div>
    </Figure>
  )
}
